/**
 * PdfTemplatesTab — Diseñador de plantillas PDF para DTEs
 * Panel izquierdo: formulario con secciones colapsables
 * Panel derecho: iframe con preview del PDF
 */
import { useState, useEffect } from 'react'
import { Plus, Trash2, Loader2, Eye, Save, ChevronDown, ChevronRight, Star, GripVertical, X, FileDown } from 'lucide-react'
import { ConfirmModal, useConfirmModal } from '@/components/ConfirmModal'
import {
  usePdfTemplates,
  useCreatePdfTemplate,
  useUpdatePdfTemplate,
  useDeletePdfTemplate,
  type PdfTemplate,
} from '@/modules/config/hooks/useConfig'
import apiClient from '@/lib/apiClient'
import { inputSmallCls, labelCompactCls } from '@/lib/formStyles'
import { EmptyState } from '@/components/EmptyState'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'
import { SegmentGroup } from '@/components/ds/SegmentGroup'
import { PrintTemplatesPanel } from './PrintTemplatesPanel'

const DOC_TYPES = [
  { value: 'FE', label: 'Factura Electrónica' },
  { value: 'BE', label: 'Boleta Electrónica' },
  { value: 'NC', label: 'Nota de Crédito' },
  { value: 'ND', label: 'Nota de Débito' },
  { value: 'FC', label: 'Factura de Compra' },
  { value: 'GD', label: 'Guía de Despacho' },
  { value: 'SI', label: 'Factura Proveedor' },
]

const PAGE_SIZES = [
  { value: 'letter', label: 'Carta (21.6x27.9cm)' },
  { value: 'legal', label: 'Oficio (21.6x33cm)' },
  { value: 'half_letter', label: 'Media Carta (21.6x14cm)' },
]

const AVAILABLE_EXTRA_FIELDS = [
  { value: 'notas', label: 'Observaciones / Notas' },
  { value: 'referencias', label: 'Referencias' },
  { value: 'personalizados', label: 'Campos Personalizados' },
  { value: 'condiciones_pago', label: 'Condiciones de Pago' },
  { value: 'contacto_receptor', label: 'Contacto del Receptor' },
  { value: 'moneda_extranjera', label: 'Información de Moneda Extranjera' },
]

const EXTRA_POSITIONS = [
  { value: 'after_receptor', label: 'Después del receptor' },
  { value: 'before_detail', label: 'Antes del detalle' },
  { value: 'after_detail', label: 'Después del detalle' },
  { value: 'after_totals', label: 'Después de los totales' },
]

interface ExtraFieldConfig {
  field: string
  position: string
  custom_title?: string
}

const inputCls = inputSmallCls
const labelCls = labelCompactCls

function CollapsibleSection({ title, children, defaultOpen = true }: {
  title: string; children: React.ReactNode; defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="border border-divider rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center justify-between w-full px-3 py-2 bg-surface-muted hover:bg-soft transition-colors"
      >
        <span className="text-xs font-semibold text-content uppercase tracking-wide">{title}</span>
        {open ? <ChevronDown size={14} className="text-content-subtle" /> : <ChevronRight size={14} className="text-content-subtle" />}
      </button>
      {open && <div className="p-3 space-y-3">{children}</div>}
    </div>
  )
}

function TemplateForm({ template, onSave, isPending }: {
  template: PdfTemplate | null
  onSave: (data: Partial<PdfTemplate>) => void
  isPending: boolean
}) {
  const layout = template?.layout_config as Record<string, Record<string, unknown>> || {}

  const [name, setName] = useState(template?.name || '')
  const [documentType, setDocumentType] = useState(template?.document_type || 'FE')
  const [additionalTypes, setAdditionalTypes] = useState<string[]>(
    (template?.additional_document_types || []).filter(t => t !== '__all__'),
  )
  const [appliesToAll, setAppliesToAll] = useState(
    (template?.additional_document_types || []).includes('__all__'),
  )
  const [pageSize, setPageSize] = useState(template?.page_size || 'letter')
  const [isDefault, setIsDefault] = useState(template?.is_default || false)
  const [customCss, setCustomCss] = useState(template?.custom_css || '')

  // Layout config sections
  const [showLogo, setShowLogo] = useState(layout.header?.show_logo !== false)
  const [logoMaxWidth, setLogoMaxWidth] = useState(Number(layout.header?.logo_max_width_pct ?? 20))
  const [logoMaxHeight, setLogoMaxHeight] = useState(Number(layout.header?.logo_max_height_mm ?? 18))
  const [logoAlign, setLogoAlign] = useState(String(layout.header?.logo_align ?? 'left'))
  const [borderColor, setBorderColor] = useState(String(layout.recuadro?.border_color ?? '#cc0000'))
  const [showSku, setShowSku] = useState(layout.detail?.show_sku !== false)
  const [showUnit, setShowUnit] = useState(layout.detail?.show_unit !== false)
  const [altRowColor, setAltRowColor] = useState(String(layout.detail?.alt_row_color ?? '#f8fafc'))
  const [footerText, setFooterText] = useState(String(layout.footer?.custom_text ?? ''))
  const [fontFamily, setFontFamily] = useState(String(layout.fonts?.family ?? 'sans-serif'))
  const [headingSize, setHeadingSize] = useState(Number(layout.fonts?.heading_size ?? 11))
  const [bodySize, setBodySize] = useState(Number(layout.fonts?.body_size ?? 9))
  const [tableSize, setTableSize] = useState(Number(layout.fonts?.table_size ?? 8))
  const [primaryColor, setPrimaryColor] = useState(String(layout.colors?.primary ?? '#1e293b'))
  const [accentColor, setAccentColor] = useState(String(layout.colors?.accent ?? '#dc2626'))
  const [extraFields, setExtraFields] = useState<ExtraFieldConfig[]>(
    (layout.extra_fields as unknown as ExtraFieldConfig[]) || []
  )
  // TED config
  const [tedColumns, setTedColumns] = useState(Number(layout.ted?.columns ?? 10))
  const [tedScale, setTedScale] = useState(Number(layout.ted?.scale ?? 2))
  const [tedRatio, setTedRatio] = useState(Number(layout.ted?.ratio ?? 3))
  const [tedMinWidth, setTedMinWidth] = useState(Number(layout.ted?.min_width_mm ?? 50))
  const [tedMinHeight, setTedMinHeight] = useState(Number(layout.ted?.min_height_mm ?? 20))

  useEffect(() => {
    if (!template) return
    const l = template.layout_config as Record<string, Record<string, unknown>> || {}
    setName(template.name || '')
    setDocumentType(template.document_type || 'FE')
    const adt = template.additional_document_types || []
    setAppliesToAll(adt.includes('__all__'))
    setAdditionalTypes(adt.filter(t => t !== '__all__'))
    setPageSize(template.page_size || 'letter')
    setIsDefault(template.is_default || false)
    setCustomCss(template.custom_css || '')
    setShowLogo(l.header?.show_logo !== false)
    setLogoMaxWidth(Number(l.header?.logo_max_width_pct ?? 20))
    setLogoMaxHeight(Number(l.header?.logo_max_height_mm ?? 18))
    setLogoAlign(String(l.header?.logo_align ?? 'left'))
    setBorderColor(String(l.recuadro?.border_color ?? '#cc0000'))
    setShowSku(l.detail?.show_sku !== false)
    setShowUnit(l.detail?.show_unit !== false)
    setAltRowColor(String(l.detail?.alt_row_color ?? '#f8fafc'))
    setFooterText(String(l.footer?.custom_text ?? ''))
    setFontFamily(String(l.fonts?.family ?? 'sans-serif'))
    setHeadingSize(Number(l.fonts?.heading_size ?? 11))
    setBodySize(Number(l.fonts?.body_size ?? 9))
    setTableSize(Number(l.fonts?.table_size ?? 8))
    setPrimaryColor(String(l.colors?.primary ?? '#1e293b'))
    setAccentColor(String(l.colors?.accent ?? '#dc2626'))
    setExtraFields((l.extra_fields as unknown as ExtraFieldConfig[]) || [])
    setTedColumns(Number(l.ted?.columns ?? 10))
    setTedScale(Number(l.ted?.scale ?? 2))
    setTedRatio(Number(l.ted?.ratio ?? 3))
    setTedMinWidth(Number(l.ted?.min_width_mm ?? 50))
    setTedMinHeight(Number(l.ted?.min_height_mm ?? 20))
  }, [template])

  const handleSubmit = () => {
    onSave({
      name,
      document_type: documentType,
      additional_document_types: appliesToAll ? ['__all__'] : additionalTypes,
      page_size: pageSize,
      is_default: isDefault,
      custom_css: customCss,
      layout_config: {
        header: {
          show_logo: showLogo,
          logo_max_width_pct: logoMaxWidth,
          logo_max_height_mm: logoMaxHeight,
          logo_align: logoAlign,
        },
        recuadro: { border_color: borderColor },
        detail: { show_sku: showSku, show_unit: showUnit, alt_row_color: altRowColor },
        totals: { show_payment_terms: true },
        footer: { custom_text: footerText },
        fonts: { family: fontFamily, heading_size: headingSize, body_size: bodySize, table_size: tableSize },
        colors: { primary: primaryColor, accent: accentColor },
        ted: { columns: tedColumns, scale: tedScale, ratio: tedRatio, min_width_mm: tedMinWidth, min_height_mm: tedMinHeight, security_level: 5 },
        extra_fields: extraFields,
      },
    })
  }

  return (
    <div className="space-y-3">
      {/* General */}
      <CollapsibleSection title="General">
        <TextField
          dense
          label="Nombre"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Mi plantilla"
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            dense
            label="Tipo Documento"
            value={documentType}
            onChange={e => setDocumentType(e.target.value)}
          >
            {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </SelectField>
          <SelectField
            dense
            label="Tamaño Página"
            value={pageSize}
            onChange={e => setPageSize(e.target.value)}
          >
            {PAGE_SIZES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </SelectField>
        </div>
        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)}
            className="rounded border-divider-strong text-primary focus:ring-primary" />
          Plantilla por defecto para este tipo
        </label>

        <div className="mt-3 p-3 rounded-lg border border-divider bg-glass-3 space-y-2">
          <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
            <input type="checkbox" checked={appliesToAll}
              onChange={e => setAppliesToAll(e.target.checked)}
              className="rounded border-divider-strong text-primary focus:ring-primary" />
            <span className="font-medium">Usar como plantilla corporativa (todos los tipos)</span>
          </label>
          <p className="text-xs text-content-subtle">
            Si está activado, esta plantilla aplica a cualquier tipo de documento que no tenga una plantilla específica.
          </p>

          {!appliesToAll && (
            <div className="pt-2">
              <label className={labelCls}>Aplicar también a estos tipos</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {DOC_TYPES.filter(t => t.value !== documentType).map(t => {
                  const checked = additionalTypes.includes(t.value)
                  return (
                    <label key={t.value} className="flex items-center gap-2 text-xs text-content cursor-pointer">
                      <input type="checkbox" checked={checked}
                        onChange={e => {
                          if (e.target.checked) setAdditionalTypes([...additionalTypes, t.value])
                          else setAdditionalTypes(additionalTypes.filter(x => x !== t.value))
                        }}
                        className="rounded border-divider-strong text-primary focus:ring-primary" />
                      {t.label}
                    </label>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* Encabezado */}
      <CollapsibleSection title="Encabezado" defaultOpen={false}>
        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={showLogo} onChange={e => setShowLogo(e.target.checked)}
            className="rounded border-divider-strong text-primary focus:ring-primary" />
          Mostrar logo
        </label>
        {showLogo && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <TextField
              dense
              label="Ancho máximo (%)"
              value={logoMaxWidth}
              onChange={e => setLogoMaxWidth(Number(e.target.value))}
              inputProps={{ type: 'number', min: 5, max: 80 }}
            />
            <TextField
              dense
              label="Alto máximo (mm)"
              value={logoMaxHeight}
              onChange={e => setLogoMaxHeight(Number(e.target.value))}
              inputProps={{ type: 'number', min: 5, max: 60 }}
            />
            <SelectField
              dense
              label="Alineación"
              value={logoAlign}
              onChange={e => setLogoAlign(e.target.value)}
            >
              <option value="left">Izquierda</option>
              <option value="center">Centrado</option>
              <option value="right">Derecha</option>
            </SelectField>
          </div>
        )}
      </CollapsibleSection>

      {/* Recuadro */}
      <CollapsibleSection title="Recuadro SII" defaultOpen={false}>
        <div>
          <label htmlFor="pdftemplatestab-color-del-borde" className={labelCls}>Color del borde</label>
          <div className="flex items-center gap-2">
            <input id="pdftemplatestab-color-del-borde" type="color" value={borderColor} onChange={e => setBorderColor(e.target.value)}
              className="w-8 h-8 rounded border border-divider cursor-pointer" />
            <input aria-label="Color del borde (hexadecimal)" value={borderColor} onChange={e => setBorderColor(e.target.value)}
              className={inputCls + ' flex-1'} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Timbre Electrónico (PDF417) */}
      <CollapsibleSection title="Timbre Electrónico (PDF417)" defaultOpen={false}>
        <p className="text-xs text-content-subtle mb-2">
          El SII requiere mínimo 2x5cm. Ajusta los parámetros para controlar el tamaño del barcode.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            dense
            label="Ancho mínimo (mm)"
            value={tedMinWidth}
            onChange={e => setTedMinWidth(Number(e.target.value))}
            inputProps={{ type: 'number', min: 50, max: 80 }}
          />
          <TextField
            dense
            label="Alto mínimo (mm)"
            value={tedMinHeight}
            onChange={e => setTedMinHeight(Number(e.target.value))}
            inputProps={{ type: 'number', min: 20, max: 50 }}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <TextField
            dense
            label="Columnas"
            value={tedColumns}
            onChange={e => setTedColumns(Number(e.target.value))}
            inputProps={{ type: 'number', min: 6, max: 20 }}
            hint="Más = más ancho"
          />
          <TextField
            dense
            label="Escala (px)"
            value={tedScale}
            onChange={e => setTedScale(Number(e.target.value))}
            inputProps={{ type: 'number', min: 1, max: 5 }}
            hint="Tamaño del módulo"
          />
          <TextField
            dense
            label="Ratio (alto:ancho)"
            value={tedRatio}
            onChange={e => setTedRatio(Number(e.target.value))}
            inputProps={{ type: 'number', min: 2, max: 5 }}
            hint="Estándar PDF417: 3"
          />
        </div>
      </CollapsibleSection>

      {/* Detalle */}
      <CollapsibleSection title="Detalle" defaultOpen={false}>
        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={showSku} onChange={e => setShowSku(e.target.checked)}
            className="rounded border-divider-strong text-primary focus:ring-primary" />
          Mostrar columna SKU
        </label>
        <label className="flex items-center gap-2 text-sm text-content cursor-pointer">
          <input type="checkbox" checked={showUnit} onChange={e => setShowUnit(e.target.checked)}
            className="rounded border-divider-strong text-primary focus:ring-primary" />
          Mostrar columna Unidad
        </label>
        <div>
          <label htmlFor="pdftemplatestab-color-filas-alternas" className={labelCls}>Color filas alternas</label>
          <div className="flex items-center gap-2">
            <input id="pdftemplatestab-color-filas-alternas" type="color" value={altRowColor} onChange={e => setAltRowColor(e.target.value)}
              className="w-8 h-8 rounded border border-divider cursor-pointer" />
            <input aria-label="Color de filas alternas (hexadecimal)" value={altRowColor} onChange={e => setAltRowColor(e.target.value)}
              className={inputCls + ' flex-1'} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Colores & Fuentes */}
      <CollapsibleSection title="Colores y Fuentes" defaultOpen={false}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="pdftemplatestab-color-principal" className={labelCls}>Color principal</label>
            <div className="flex items-center gap-2">
              <input id="pdftemplatestab-color-principal" type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)}
                className="w-8 h-8 rounded border border-divider cursor-pointer" />
              <input aria-label="Color principal (hexadecimal)" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className={inputCls + ' flex-1'} />
            </div>
          </div>
          <div>
            <label htmlFor="pdftemplatestab-color-acento" className={labelCls}>Color acento</label>
            <div className="flex items-center gap-2">
              <input id="pdftemplatestab-color-acento" type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                className="w-8 h-8 rounded border border-divider cursor-pointer" />
              <input aria-label="Color de acento (hexadecimal)" value={accentColor} onChange={e => setAccentColor(e.target.value)} className={inputCls + ' flex-1'} />
            </div>
          </div>
        </div>
        <SelectField
          dense
          label="Familia tipográfica"
          value={fontFamily}
          onChange={e => setFontFamily(e.target.value)}
        >
          <option value="sans-serif">Sans-serif</option>
          <option value="serif">Serif</option>
          <option value="monospace">Monospace</option>
        </SelectField>
        <div className="grid grid-cols-3 gap-3">
          <TextField
            dense
            label="Título (pt)"
            value={headingSize}
            onChange={e => setHeadingSize(Number(e.target.value))}
            inputProps={{ type: 'number', min: 8, max: 16 }}
          />
          <TextField
            dense
            label="Cuerpo (pt)"
            value={bodySize}
            onChange={e => setBodySize(Number(e.target.value))}
            inputProps={{ type: 'number', min: 6, max: 14 }}
          />
          <TextField
            dense
            label="Tabla (pt)"
            value={tableSize}
            onChange={e => setTableSize(Number(e.target.value))}
            inputProps={{ type: 'number', min: 6, max: 12 }}
          />
        </div>
      </CollapsibleSection>

      {/* Pie */}
      <CollapsibleSection title="Pie de Página" defaultOpen={false}>
        <TextField
          dense
          label="Texto personalizado"
          multiline
          rows={2}
          value={footerText}
          onChange={e => setFooterText(e.target.value)}
          placeholder="Texto opcional al pie del documento"
        />
      </CollapsibleSection>

      {/* Campos Adicionales */}
      <CollapsibleSection title="Campos Adicionales" defaultOpen={false}>
        <p className="text-xs text-content-subtle mb-2">
          Añade campos del documento en posiciones específicas del PDF.
        </p>

        {extraFields.map((ef, idx) => (
          <div key={idx} className="flex items-start gap-2 p-2 bg-glass-3 rounded-lg border border-divider">
            <GripVertical size={14} className="text-content-subtle mt-1.5 shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <SelectField
                  dense
                  label="Campo"
                  value={ef.field}
                  onChange={e => {
                      const updated = [...extraFields]
                      updated[idx] = { ...updated[idx], field: e.target.value }
                      setExtraFields(updated)
                    }}
                >
                  <option value="">Seleccionar...</option>
                  {AVAILABLE_EXTRA_FIELDS.map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </SelectField>
                <SelectField
                  dense
                  label="Posición"
                  value={ef.position}
                  onChange={e => {
                      const updated = [...extraFields]
                      updated[idx] = { ...updated[idx], position: e.target.value }
                      setExtraFields(updated)
                    }}
                >
                  {EXTRA_POSITIONS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </SelectField>
              </div>
              <TextField
                dense
                label="Título personalizado (opcional)"
                value={ef.custom_title || ''}
                onChange={e => {
                    const updated = [...extraFields]
                    updated[idx] = { ...updated[idx], custom_title: e.target.value }
                    setExtraFields(updated)
                  }}
                placeholder="Deja vacío para usar el título por defecto"
              />
            </div>
            <button
              onClick={() => setExtraFields(extraFields.filter((_, i) => i !== idx))}
              className="p-1 text-content-subtle hover:text-danger-solid transition-colors shrink-0 mt-1"
              title="Eliminar"
              aria-label="Eliminar campo"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        <button
          onClick={() => setExtraFields([...extraFields, { field: '', position: 'after_detail' }])}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-hover bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors w-full justify-center"
        >
          <Plus size={13} />Añadir campo
        </button>
      </CollapsibleSection>

      {/* CSS Personalizado */}
      <CollapsibleSection title="CSS Personalizado" defaultOpen={false}>
        <TextField
          dense
          label="CSS adicional (avanzado)"
          multiline
          rows={4}
          value={customCss}
          onChange={e => setCustomCss(e.target.value)}
          placeholder=".detail-table { ... }"
        />
      </CollapsibleSection>

      <button
        onClick={handleSubmit}
        disabled={isPending || !name.trim()}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
      >
        {isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
        {template ? 'Guardar cambios' : 'Crear plantilla'}
      </button>
    </div>
  )
}

/**
 * Las simples (logo, colores y bloques sobre el diseño de Clobi) y las avanzadas
 * (elementos posicionados sobre el XML firmado, PLAN_SUTEX_Y_PLANTILLAS) conviven:
 * una avanzada publicada para el tipo gana; sin ella, sale la simple.
 */
export function PdfTemplatesTab() {
  const [tipo, setTipo] = useState<'simples' | 'avanzadas'>('simples')
  return (
    <div className="space-y-4">
      <SegmentGroup
        label="Tipo de plantilla"
        size="sm"
        value={tipo}
        onChange={setTipo}
        options={[
          { value: 'simples', label: 'Plantillas simples', hint: 'Logo, colores y bloques sobre el diseño de Clobi' },
          { value: 'avanzadas', label: 'Plantillas avanzadas', hint: 'Elementos posicionados sobre el XML firmado del SII' },
        ]}
      />
      {tipo === 'simples' ? <PlantillasSimples /> : <PrintTemplatesPanel />}
    </div>
  )
}

function PlantillasSimples() {
  const { data: templates = [], isLoading } = usePdfTemplates()
  const createMutation = useCreatePdfTemplate()
  const updateMutation = useUpdatePdfTemplate()
  const deleteMutation = useDeletePdfTemplate()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const { confirm: confirmAction, modalProps } = useConfirmModal()

  const selectedTemplate = templates.find(t => t.id === selectedId) || null

  const handleCreate = (data: Partial<PdfTemplate>) => {
    createMutation.mutate(data, {
      onSuccess: (newTpl) => {
        setCreating(false)
        setSelectedId(newTpl.id)
      },
    })
  }

  const handleUpdate = (data: Partial<PdfTemplate>) => {
    if (!selectedId) return
    updateMutation.mutate({ id: selectedId, data }, {
      onSuccess: () => setPreviewUrl(null),
    })
  }

  const handleDelete = (id: string) => {
    confirmAction({
      message: '¿Eliminar esta plantilla?',
      variant: 'danger',
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => {
            if (selectedId === id) {
              setSelectedId(null)
              setPreviewUrl(null)
            }
          },
        })
      },
    })
  }

  const handlePreview = async (id: string) => {
    setLoadingPreview(true)
    try {
      const res = await apiClient.get(`/config/pdf-templates/${id}/preview/`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      setPreviewUrl(url)
    } catch {
      setPreviewUrl(null)
    } finally {
      setLoadingPreview(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-content-subtle" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <ConfirmModal {...modalProps} />
      {/* Template list */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-content-muted">{templates.length} plantilla{templates.length !== 1 ? 's' : ''}</p>
        <button
          onClick={() => { setCreating(true); setSelectedId(null); setPreviewUrl(null) }}
          className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover transition-colors"
        >
          <Plus size={14} />Nueva plantilla
        </button>
      </div>

      {/* Template cards */}
      {templates.length > 0 && !creating && !selectedId && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {templates.map(tpl => (
            <div
              key={tpl.id}
              onClick={() => { setSelectedId(tpl.id); setCreating(false); setPreviewUrl(null) }}
              className="relative p-4 bg-glass backdrop-blur-glass rounded-card border border-divider hover:border-primary-200 hover:shadow-sm cursor-pointer transition-all group shadow-card"
            >
              {tpl.is_default && (
                <Star size={14} className="absolute top-3 right-3 text-warning-solid fill-amber-500" />
              )}
              <div className="flex flex-wrap items-center gap-1 mb-2">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold font-mono bg-surface-muted text-content-muted">
                  {tpl.document_type}
                </span>
                {(tpl.additional_document_types || []).filter(t => t !== '__all__').map(t => (
                  <span key={t} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-mono bg-surface-muted text-content-subtle">
                    +{t}
                  </span>
                ))}
                {(tpl.additional_document_types || []).includes('__all__') && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-primary-50 text-primary-hover">
                    Corporativa
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-content truncate">{tpl.name}</p>
              <p className="text-xs text-content-subtle mt-1">{tpl.page_size_display}</p>
              <div className="flex items-center gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={e => { e.stopPropagation(); handlePreview(tpl.id) }}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-primary hover:bg-primary-50 rounded transition-colors"
                >
                  <Eye size={11} />Preview
                </button>
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(tpl.id) }}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-danger-content-soft hover:bg-danger-surface rounded transition-colors"
                >
                  <Trash2 size={11} />Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Editor view (create or edit) */}
      {(creating || selectedId) && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => { setCreating(false); setSelectedId(null); setPreviewUrl(null) }}
              className="text-sm text-primary hover:text-primary-hover transition-colors"
            >
              &larr; Volver a la lista
            </button>
            {selectedId && (
              <button
                onClick={() => handlePreview(selectedId)}
                disabled={loadingPreview}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-primary-hover bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 disabled:opacity-60 transition-colors"
              >
                {loadingPreview ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
                Vista previa
              </button>
            )}
          </div>

          {/* Split layout: form scrollable / preview sticky */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:h-[calc(100vh-240px)] lg:min-h-[500px]">
            {/* Form panel — scrollable */}
            <div className="space-y-3 lg:overflow-y-auto lg:pr-2">
              <TemplateForm
                template={creating ? null : selectedTemplate}
                onSave={creating ? handleCreate : handleUpdate}
                isPending={creating ? createMutation.isPending : updateMutation.isPending}
              />
            </div>

            {/* Preview panel — sticky */}
            <div className="bg-surface-muted rounded-xl border border-divider overflow-hidden min-h-[500px] lg:h-full flex items-center justify-center lg:sticky lg:top-0">
              {previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full min-h-[500px]"
                  title="PDF Preview"
                />
              ) : (
                <div className="text-center text-content-subtle p-8">
                  <Eye size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">
                    {selectedId
                      ? 'Hacer clic en "Vista previa" para ver el PDF'
                      : 'Guarda la plantilla para generar vista previa'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {templates.length === 0 && !creating && (
        <EmptyState
          icon={FileDown}
          title="No hay plantillas PDF"
          hint="Crea una plantilla para personalizar tus documentos."
          size="md"
        />
      )}
    </div>
  )
}

